/**
 * External benchmarking (brief 9.13, MET-12), implemented behind the
 * applyBenchmarking stub in engine.ts.
 *
 * External benchmarking aligns the population-weighted average of a
 * cell's area estimates with a target. The target is supplied by the
 * caller; the analysis layer supplies the population-weighted direct
 * estimate of the same cell — sum_i omega_i * phat_i over the cell's
 * in-sample areas, with the same population weights omega_i the
 * constraint uses (directive C-5, review item R9: the target and the
 * constraint now rest on one weighting; the earlier target, the
 * survey-weighted statewide domain, could disagree with the
 * constraint whenever weighted totals differed from population
 * counts). With population weights omega_i = N_i / sum_j N_j over
 * the areas included in the output:
 *
 * - 'logit' (the brief's default): clamp each estimate to
 *   [1e-6, 1 - 1e-6], then find the shift delta with
 *     sum_i omega_i * expit(logit(p_i) + delta) = P_target
 *   and set p_bench_i = expit(logit(p_i) + delta). The left side is
 *   strictly increasing in delta, so bracketed bisection on
 *   [-50, 50] solves it deterministically to near machine precision;
 *   the shift keeps every result inside (0, 1).
 * - 'ratio': p_bench_i = p_i * P_target / (sum_j omega_j p_j). The
 *   brief rejects ratio benchmarking for a cell when any result
 *   exceeds 1. That outcome cannot be expressed in the result type,
 *   so this function refuses it loudly with a descriptive error (the
 *   engine's standing pattern for inexpressible outcomes — compare
 *   the fpc refusal in direct.ts); the analysis layer catches it per
 *   cell, records an issue, and leaves the cell unbenchmarked.
 *
 * After benchmarking, intervals are recomputed with the estimate's
 * carried mse_theta_i, as brief 9.13 directs ("Quick mode uses the
 * unbenchmarked analytical MSE"), centered by the SAME convention as
 * the unbenchmarked interval (directive C-5, review item R9): the
 * center is the fitted theta thetatilde_i plus the displacement
 * benchmarking applied to the point estimate, measured on the fitted
 * scale — asin(sqrt(p_bench_i)) - asin(sqrt(ptilde_i)) on the arcsine
 * scale, p_bench_i - ptilde_i on the linear scale. A zero shift
 * therefore reproduces the unbenchmarked interval exactly, which the
 * previous convention (centering on asin(sqrt(p_bench_i))) did not:
 * it moved every interval by the bias-correction's own displacement.
 * The fitted theta is read from the estimate's carried eblupTheta;
 * estimates built by hand without one have their center recovered
 * from their own interval (exact for intervals built by brief 9.10
 * without end-clamping). The rmse follows brief 9.10 on the arcsine
 * scale (the interval-based standard error) and sqrt(mse_theta) on
 * the linear scale (directive C-3). Estimates that do not carry
 * mseTheta cannot have their intervals recomputed and are refused.
 * Benchmarked results carry benchmarkingMethod so the method is
 * named in the output (brief 12.3 model metadata; decision S5
 * requires the method to be named whenever benchmarking is on).
 *
 * Internal consistency (brief 9.13, optional) is NOT implemented
 * here: it shifts subgroup levels within an area toward the area's
 * overall estimate using population shares per level, and no input
 * contract carries those shares — see validation/parity/
 * OPEN_QUESTIONS.md.
 */
import { normalQuantile } from './distributions.js';
import { proportionScaleMseR } from './backtransform.js';
import { clamp, inverseLogit, logit } from './numeric.js';
const HALF_PI = Math.PI / 2;
const LOGIT_CLAMP = 1e-6;
function clampTheta(t) {
    return Math.min(Math.max(t, 0), HALF_PI);
}
/**
 * Benchmark point estimates against a target under precomputed
 * weights (omega_i >= 0, not all zero). Shared by applyBenchmarking
 * (weights from population counts) and the bootstrap (brief 9.13
 * inside-replicate benchmarking). Returns new point estimates in
 * input order; throws on the ratio rejection of brief 9.13.
 */
export function benchmarkPointEstimates(values, omegas, method, target) {
    if (values.length !== omegas.length) {
        throw new Error(`benchmarking: ${values.length} estimates but ${omegas.length} weights.`);
    }
    if (method === 'ratio') {
        let weightedMean = 0;
        for (let i = 0; i < values.length; i++)
            weightedMean += omegas[i] * values[i];
        if (!(weightedMean > 0)) {
            throw new Error('applyBenchmarking: ratio benchmarking is impossible — the ' +
                'population-weighted mean of the estimates is 0 while the ' +
                `target is ${target}.`);
        }
        const factor = target / weightedMean;
        const out = values.map((v) => v * factor);
        for (let i = 0; i < out.length; i++) {
            if (out[i] > 1) {
                throw new Error('applyBenchmarking: ratio benchmarking rejected for this cell — ' +
                    `benchmarking would push area ${i} to ${out[i]}, above 1 ` +
                    '(brief 9.13). Use the logit-shift method for this cell.');
            }
        }
        return out;
    }
    // Logit shift: solve sum_i omega_i expit(logit(p_i) + delta) = target.
    const logits = values.map((v) => logit(clamp(v, LOGIT_CLAMP, 1 - LOGIT_CLAMP)));
    const shiftedMean = (delta) => {
        let sum = 0;
        for (let i = 0; i < values.length; i++) {
            sum += omegas[i] * inverseLogit(logits[i] + delta);
        }
        return sum;
    };
    let lo = -50;
    let hi = 50;
    // f is increasing; f(lo) < target < f(hi) for any target in (0, 1)
    // because expit saturates at the bracket ends.
    for (let it = 0; it < 200; it++) {
        const mid = 0.5 * (lo + hi);
        if (shiftedMean(mid) < target)
            lo = mid;
        else
            hi = mid;
        if (hi - lo < 1e-14)
            break;
    }
    const delta = 0.5 * (lo + hi);
    return values.map((_, i) => inverseLogit(logits[i] + delta));
}
/**
 * Recompute intervals around benchmarked point estimates with the
 * estimates' carried mse_theta_i (brief 9.13, 9.10), returning new
 * estimate objects stamped with the benchmarking method.
 */
export function benchmarkEstimates(estimates, omegas, method, target, confidence, transform = 'arcsin', reportedPrecisionR) {
    for (const e of estimates) {
        if (e.mseTheta === undefined || !Number.isFinite(e.mseTheta)) {
            throw new Error(`applyBenchmarking: estimate for area "${e.areaId}" carries no ` +
                'mse_theta, so its interval cannot be recomputed after ' +
                'benchmarking (brief 9.13). Benchmark the output of ' +
                'backTransformAreas (or Full-mode estimates assembled from the ' +
                'bootstrap MSEs), which carry mse_theta.');
        }
    }
    const points = benchmarkPointEstimates(estimates.map((e) => e.estimate), omegas, method, target);
    const z = normalQuantile(0.5 + confidence / 2);
    return estimates.map((e, i) => {
        const estimate = points[i];
        const mseTheta = e.mseTheta;
        const halfWidth = z * Math.sqrt(Math.max(0, mseTheta));
        // The interval is recomputed on the scale the estimates were
        // fitted on (brief 9.5/9.10; Q44), centered by the C-5 convention
        // (module header): the unbenchmarked center plus benchmarking's
        // displacement of the point estimate on the fitted scale.
        let lower;
        let upper;
        let rmse;
        if (transform === 'linear') {
            const center0 = e.eblupTheta ?? (clamp(e.lower, 0, 1) + clamp(e.upper, 0, 1)) / 2;
            const center = clamp(center0 + (estimate - e.estimate), 0, 1);
            lower = clamp(center - halfWidth, 0, 1);
            upper = clamp(center + halfWidth, 0, 1);
            // Directive C-3: sqrt(mse_theta) on the linear scale.
            rmse = Math.sqrt(Math.max(0, mseTheta));
        }
        else {
            const thetaOf = (p) => Math.asin(Math.sqrt(clamp(p, 0, 1)));
            const center0 = e.eblupTheta ??
                (thetaOf(e.lower) + thetaOf(e.upper)) / 2;
            const center = clampTheta(center0 + (thetaOf(estimate) - thetaOf(e.estimate)));
            lower = Math.sin(clampTheta(center - halfWidth)) ** 2;
            upper = Math.sin(clampTheta(center + halfWidth)) ** 2;
            // Workstream R (study switch): the reported precision is
            // sqrt(MSE_p) at the benchmarked center, the registration's
            // form (the benchmarking shift applied on the arcsine scale).
            rmse =
                reportedPrecisionR === true
                    ? Math.sqrt(proportionScaleMseR(center, mseTheta, estimate))
                    : (upper - lower) / (2 * z);
        }
        const cv = estimate > 0 ? rmse / estimate : rmse > 0 ? Number.POSITIVE_INFINITY : 0;
        return {
            areaId: e.areaId,
            estimate,
            lower,
            upper,
            rmse,
            cv,
            estimateOutsideInterval: estimate < lower || estimate > upper,
            mseTheta,
            ...(e.eblupTheta !== undefined ? { eblupTheta: e.eblupTheta } : {}),
            benchmarkingMethod: method,
        };
    });
}
/**
 * External benchmarking against a target with population counts
 * (brief 9.13). Population counts are required for every area in the
 * output; a missing or non-positive count is a descriptive error,
 * not a silent skip — benchmarking to the wrong weights would
 * misalign the cell without any trace.
 */
export function applyBenchmarkingImpl(estimates, options) {
    if (estimates.length === 0)
        return [];
    if (!(options.target >= 0 && options.target <= 1)) {
        throw new Error(`applyBenchmarking: target must be a proportion in [0, 1]; received ${options.target}.`);
    }
    if (options.method === 'logit' && (options.target <= 0 || options.target >= 1)) {
        throw new Error(`applyBenchmarking: the logit-shift method needs a target strictly ` +
            `inside (0, 1); received ${options.target}.`);
    }
    const counts = estimates.map((e) => {
        const n = options.populationCounts[e.areaId];
        if (n === undefined || !Number.isFinite(n) || n <= 0) {
            throw new Error(`applyBenchmarking: no usable population count for area ` +
                `"${e.areaId}" — benchmarking requires area population counts ` +
                'for every area in the output (brief 9.13).');
        }
        return n;
    });
    const total = counts.reduce((a, b) => a + b, 0);
    const omegas = counts.map((n) => n / total);
    return benchmarkEstimates(estimates, omegas, options.method, options.target, options.confidence ?? 0.95, options.transform ?? 'arcsin', options.reportedPrecisionR ?? false);
}
//# sourceMappingURL=benchmarking.js.map